import {
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import AddStepButtons from "./AddStepButton";
import {
  parseExtensionToSteps,
  generateExtensionPreviewText,
  validateSteps,
} from "./extensionUtils";
import Step from "./Step";

const ExtensionStep = ({ extension, onCommit, onCancel }) => {
  const parseExistingExtension = (extensionStr) => {
    return parseExtensionToSteps(extensionStr);
  };

  const [steps, setSteps] = useState(() => {
    const parsed = parseExistingExtension(extension);
    return parsed.length > 0 ? parsed : [];
  });

  const stepsContainerRef = useRef(null);

  // Auto-scroll to bottom when steps are added
  useEffect(() => {
    if (stepsContainerRef.current) {
      stepsContainerRef.current.scrollTop =
        stepsContainerRef.current.scrollHeight;
    }
  }, [steps.length]);

  const addPressStep = () => {
    const newStep = {
      type: "press",
      digit: "",
      id: uuidv4(),
    };
    setSteps([...steps, newStep]);
  };

  const addWaitStep = () => {
    const newStep = {
      type: "wait",
      waitSeconds: 3, // Default wait time
      id: uuidv4(),
    };
    setSteps([...steps, newStep]);
  };

  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const isValid = validateSteps(steps);

  const handleCommit = () => {
    if (isValid || steps.length === 0) {
      const extensionArray = steps.map((step) => {
        if (step.type === "press") {
          return { press: step.digit.trim() };
        } else {
          return { wait: step.waitSeconds };
        }
      });

      if (extensionArray.length > 0) {
        onCommit(extensionArray);
      } else {
        onCommit(null);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-sm font-bold text-secondary">
          Build your extension sequence step by step. Add button
          presses and wait times as needed.
        </p>
      </div>

      <div className="space-y-4">
        {steps.length > 0 ? (
          <div
            ref={stepsContainerRef}
            className="space-y-3 max-h-64 overflow-y-auto"
          >
            {steps.map((step, index) => (
              <Step
                key={step.id}
                stepNumber={index + 1}
                type={step.type}
                value={
                  step.type === "press"
                    ? step.digit
                    : step.waitSeconds
                }
                onChange={(newValue) => {
                  const field =
                    step.type === "press" ? "digit" : "waitSeconds";
                  updateStep(index, field, newValue);
                }}
                onRemove={() => removeStep(index)}
              />
            ))}
          </div>
        ) : (
          <DSCardWrapper variant="info">
            <div className="flex flex-row items-center gap-xs text-center space-y-1">
              <FontAwesomeIcon icon={faInfoCircle} />
              <p className="text-xs">
                If you need help getting started, try calling your own
                number to learn what extension you need to add!
              </p>
            </div>
          </DSCardWrapper>
        )}

        {steps.length < 10 ? (
          <div className="text-center py-4">
            <AddStepButtons
              onAddPressStep={addPressStep}
              onAddWaitStep={addWaitStep}
            />
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-secondary">
              You've reached the maximum number of steps.
            </p>
          </div>
        )}

        {steps.length > 0 && isValid && (
          <DSCardWrapper variant="info">
            <p className="text-sm text-brand">
              <strong>Extension Preview:</strong>{" "}
              {generateExtensionPreviewText(steps)}
            </p>
          </DSCardWrapper>
        )}

        {!isValid && steps.length > 0 && (
          <DSCardWrapper variant="warning">
            <p className="text-xs text-warning">
              Press steps must have numbers only or be empty. Wait
              steps must be between 0-60 seconds.
            </p>
          </DSCardWrapper>
        )}
      </div>

      <div className="flex flex-row items-center justify-center space-x-3">
        <DSButton variant="cancel" size="md" onClick={onCancel}>
          Cancel
        </DSButton>
        <DSButton
          onClick={handleCommit}
          disabled={!isValid && steps.length > 0}
        >
          Save
        </DSButton>
      </div>
    </div>
  );
};

export default ExtensionStep;
